import React, { useEffect, useState } from "react";

import { API_URL } from "src/service/config";
import { ILanguageFillter } from "src/types";
import Layout from "@components/layout";
import { NavLink } from "react-router-dom";
import { fetchAllLanguages } from "src/service/api-services/api-services";
import { getAssetUrl } from "@utils/id.generator";
import showToast from "@utils/toast";

type Asset = {
  localText: string;
  translation: string;
  code: string;
  language: string;
  id?: string; // present only when editing existing
};

type ResponseResult = {
  saved: Asset[];
  skipped: Asset[];
};

const defaultAsset: Asset = {
  localText: "",
  translation: "",
  code: "",
  language: "",
};

export type AssetType = "audio" | "video";

interface AssetDeclarationFormProps {
  assetType: string;
  initialAssets?: Asset[];
}

const AssetDeclarationForm: React.FC<AssetDeclarationFormProps> = ({
  initialAssets = [],
  assetType,
}) => {
  // Initialize with incoming data or a single blank row
  const [assets, setAssets] = useState<Asset[]>(
    initialAssets.length > 0 ? initialAssets : [defaultAsset]
  );
  const [result, setResult] = useState<ResponseResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [Listlanguages, setLanguages] = useState<ILanguageFillter>({
    isLoading: true,
    languages: [],
  });

  useEffect(() => {
    fetchAllLanguages()
      .then((data) => {
        const { status, result } = data;
        if (status !== "success") {
          setLanguages({ isLoading: false, languages: [] });
          return showToast("Can't fetch languages at the moment", false);
        }
        setLanguages({ isLoading: false, languages: result });
      })
      .catch((error) => {
        const message =
          error instanceof Error
            ? error?.message
            : "Apologies there was an error fetching languages!";
        setLanguages({ isLoading: false, languages: [] });
        return showToast(message, false);
      });
  }, []);

  useEffect(() => {
    if (initialAssets.length) {
      setAssets(initialAssets);
    }
  }, [initialAssets]);

  const handleChange = (index: number, field: keyof Asset, value: string) => {
    const copy = [...assets];
    copy[index][field] = value;
    setAssets(copy);
  };

  const handleRemove = (index: number) => {
    if (assets.length === 1) return;
    const copy = [...assets];
    copy.splice(index, 1);
    setAssets(copy);
  };

  const addAsset = () => {
    setAssets((prev) => [...prev, { ...defaultAsset }]);
  };

  const allAssetPage =
    assetType === "audio" ? "/dashboard/audios" : "/dashboard/videos";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // If any item has an id → we're updating
      const method = assets.some((a) => a.id) ? "PUT" : "POST";

      const endpoint =
        assetType === "audio"
          ? "audios/declare-audios"
          : "videos/declare-videos";

      const url = `${API_URL}/${endpoint}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(assets),
      });

      const json = await res.json();
      setResult(json.result);
      showToast(json.message || "Assets submitted successfully!", true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      showToast(message, false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow-md mt-5">
        <form onSubmit={handleSubmit}>
          <h2 className="text-xl font-semibold mb-2 text-center">
            {assets.some((a) => a.id)
              ? "Edit Assets"
              : `Declare ${
                  assetType.charAt(0).toUpperCase() + assetType.slice(1)
                } Assets`}
          </h2>

          <NavLink
            to={allAssetPage}
            className={"justify-end items-center flex pb-4"}
          >
            <button className="mt-3 bg-brand text-white px-5 py-2 text-sm rounded hover:opacity-90 transition">
              View All {assetType.charAt(0).toUpperCase() + assetType.slice(1)}{" "}
              Assets
            </button>
          </NavLink>

          {assets.map((asset, i) => (
            <>
              <div
                key={i}
                className="relative grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 bg-gray-100 border border-gray-100 p-4 rounded shadow-lg"
              >
                <input
                  type="text"
                  placeholder="Local Text"
                  value={asset.localText}
                  onChange={(e) => handleChange(i, "localText", e.target.value)}
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Translation"
                  value={asset.translation}
                  onChange={(e) =>
                    handleChange(i, "translation", e.target.value)
                  }
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Code"
                  value={asset.code}
                  minLength={6}
                  maxLength={6}
                  onChange={(e) => handleChange(i, "code", e.target.value)}
                  required
                  className="form-input"
                />
                <select
                  value={asset.language}
                  onChange={(e) => handleChange(i, "language", e.target.value)}
                  required
                  className="form-input"
                >
                  {Listlanguages.isLoading ? (
                    <option value="" disabled>
                      Loading languages...
                    </option>
                  ) : (
                    <option value="" disabled>
                      Select Language
                    </option>
                  )}

                  {Listlanguages.languages.map((lang) => (
                    <option key={lang.code} value={lang.name.toLowerCase()}>
                      {lang.name}
                    </option>
                  ))}
                </select>

                {/* Remove button */}
                {assets.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemove(i)}
                    className="absolute top-0 right-0 mt-1 mr-1 text-red-500 hover:text-red-700"
                    aria-label="Remove row"
                  >
                    ✖
                  </button>
                )}
              </div>
            </>
          ))}

          <div className="flex items-center gap-4 mb-6">
            <button
              type="button"
              onClick={addAsset}
              className="text-brand hover:underline"
            >
              + Add another asset
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark"
            >
              {submitting
                ? "Submitting..."
                : assets.some((a) => a.id)
                ? "Update Assets"
                : "Submit Assets"}
            </button>
          </div>
        </form>

        {result && (
          <div className="mt-8 space-y-6">
            {/* Saved */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Saved Assets</h3>
              <div className="flex overflow-x-auto gap-4 pb-2">
                {result.saved.map((item, idx) => (
                  <div
                    key={idx}
                    className="min-w-[180px] p-3 bg-green-50 rounded shadow shrink-0"
                  >
                    <p className="font-bold text-green-800">
                      {item.translation}
                    </p>
                    <p className="text-sm text-gray-700">{item.localText}</p>
                    <p className="text-xs text-green-600">
                      {item.code.toUpperCase()} • {item.language}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Skipped */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Skipped Assets</h3>
              <div className="flex overflow-x-auto gap-4 pb-2">
                {result.skipped.map((item, idx) => (
                  <div
                    key={idx}
                    className="min-w-[180px] p-3 bg-red-50 rounded shadow shrink-0"
                  >
                    <p className="font-bold text-red-800">{item.translation}</p>
                    <p className="text-sm text-gray-700">{item.localText}</p>
                    <p className="text-xs text-red-600">
                      {item.code.toUpperCase()} • {item.language}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssetDeclarationForm;
